import React, { useCallback, useState } from 'react';
import Loader from 'react-loader-spinner';
import { waitFor } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';
import { useSubmitter } from '../../platform/Hook';
import { Button, ButtonVariant, Prompt, remInPx } from '../../ui';
import { ResultInfoForSubmitter } from '../../ui/ResultInfo';
import { Flexer } from '../dashboard/elements/LayoutElements';
import { Popup, PopupDefinition, PopupProps } from './Popup';

export type OptionRole = 'confirm' | 'cancel';
export type OptionDefinition = [any, string, ButtonVariant?, OptionRole?];

export const optionsPopupWith = (title: string, message: string, options: OptionDefinition[]): PopupDefinition => ({
    ui: OptionsPopup,
    args: { title, message, options },
});

export const confirmPopupWith = (title: string, message: string) => optionsPopupWith(title, message, [
    [true, 'Yes', 'primary', 'confirm'],
    [false, 'No', 'common', 'cancel'],
]);

export const noticePopupWith = (title: string, message: string) => optionsPopupWith(title, message, [
    [true, 'Close', 'common', 'cancel'],
]);

export const OptionsPopup: LFC<PopupProps> = ({ action, args }) => {
    const { title, message, options } = args;

    const onPromptAction = useCallback((confirm?: boolean) => {
        const role = confirm ? 'confirm' : 'cancel';
        const opt = options.find(x => x[3] === role);
        if (opt) action(opt[0]);
    }, [action, options]);

    return <Prompt onAction={onPromptAction}>
        <Popup w={32}>
            <Popup.Close onAction={() => onPromptAction(false)} />
            <Popup.Title>{title}</Popup.Title>
            <Popup.Content>{message}</Popup.Content>
            <Popup.Actions>
                {options.map(([value, label, variant]) =>
                    <Button key={label} variant={variant} label={label} onAction={() => action(value)} />
                )}
            </Popup.Actions>
        </Popup>
    </Prompt>;
};


export type AsyncOption = [string, ButtonVariant, () => Promise<any>];

export const AsyncOptionsPopup: LFC<PopupProps> = ({ action, dismiss, args }) => {
    const { title, message, options } = args;

    const [working, setWorking] = useState<AsyncOption>();
    const [onSubmit, _, result] = useSubmitter(async (opt: AsyncOption) => {
        setWorking(opt);
        await waitFor(100);
        try {
            action(await opt[2]());
        }
        finally {
            setWorking(null);
        }
    }, [action]);

    const onPromptAction = useCallback((confirm?: boolean) => {
        if (confirm) {
            if (options.length === 1) {
                onSubmit(options[0]);
            }
        }
        else {
            dismiss();
        }
    }, [action, options]);

    return <Prompt onAction={onPromptAction}>
        <Popup w={32}>
            <Popup.Close onAction={() => onPromptAction(false)} />
            <Popup.Title>{title}</Popup.Title>
            <Popup.Content>
                <Flexer>{message}</Flexer>
                <ResultInfoForSubmitter result={result} />
            </Popup.Content>
            <Popup.Actions>
                {options.map(opt =>
                    <Button key={opt[0]} disabled={!!working} variant={opt[1]} onAction={() => onSubmit(opt)}>
                        {opt[0]}
                        &nbsp;
                        {opt === working && <>
                            &nbsp;
                            <Loader
                                type="TailSpin"
                                color="#fff"
                                height={remInPx(1.2)}
                                width={remInPx(1.2)}
                            />
                        </>
                        }
                    </Button>
                )}
                <Button disabled={!!working} label="Cancel" onAction={() => dismiss()} />
            </Popup.Actions>
        </Popup>
    </Prompt>;
};

export const asyncOptionsPopupWith = (title: string, message: string, options: AsyncOption[]): PopupDefinition => ({
    ui: AsyncOptionsPopup,
    args: { title, message, options },
});
